/**
 * Shared types for PDF Sign application
 * Used by both frontend (web) and backend (api)
 */

// ============================================
// User & Authentication
// ============================================

export interface User {
  id: string;
  email: string;
  full_name: string;
  avatar_url?: string;
  role: UserRole;
  organization_id?: string;
  created_at: string;
  updated_at: string;
  last_login_at?: string;
  is_active: boolean;
}

export type UserRole = 'admin' | 'manager' | 'member' | 'viewer';

export interface Session {
  id: string;
  user_id: string;
  token: string;
  expires_at: string;
  created_at: string;
  ip_address?: string;
  user_agent?: string;
}

export interface MagicLinkToken {
  id: string;
  email: string;
  token: string;
  purpose: 'login' | 'verify_email' | 'reset_password';
  expires_at: string;
  used_at?: string;
  created_at: string;
}

// ============================================
// Organization & Members
// ============================================

export interface Organization {
  id: string;
  name: string;
  slug: string;
  logo_url?: string;
  settings: OrganizationSettings;
  created_at: string;
  updated_at: string;
}

export interface OrganizationSettings {
  allow_public_signing: boolean;
  default_workflow: 'sequential' | 'parallel';
  require_2fa: boolean;
  signature_types_enabled: SignatureType[];
  retention_days: number;
}

export interface OrganizationMember {
  id: string;
  organization_id: string;
  user_id: string;
  role: MemberRole;
  invited_by?: string;
  invited_at: string;
  joined_at?: string;
  status: 'pending' | 'active' | 'revoked';
}

export type MemberRole = 'owner' | 'admin' | 'editor' | 'signer' | 'viewer';

// ============================================
// Document Types
// ============================================

export type DocumentType =
  | 'bao_cao'      // Báo cáo
  | 'quyet_dinh'   // Quyết định
  | 'to_trinh'     // Tờ trình
  | 'cong_van'     // Công văn
  | 'hop_dong'     // Hợp đồng
  | 'khac';        // Khác

export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  bao_cao: 'Báo cáo',
  quyet_dinh: 'Quyết định',
  to_trinh: 'Tờ trình',
  cong_van: 'Công văn',
  hop_dong: 'Hợp đồng',
  khac: 'Khác',
};

export interface Document {
  id: string;
  organization_id: string;
  title: string;
  description?: string;
  type: DocumentType;
  status: DocumentStatus;
  workflow_type: WorkflowType;
  current_step: number;
  total_steps: number;
  file_r2_key: string;
  file_name: string;
  file_size: number;
  file_hash: string; // SHA-256 for integrity
  pages_count: number;
  created_by: string;
  /** Joined from users when the API returns a document detail */
  creator_name?: string;
  creator_email?: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  expires_at?: string;
  settings: DocumentSettings;
}

export type DocumentStatus =
  | 'draft'           // Nháp
  | 'pending_sign'    // Chờ ký
  | 'in_progress'     // Đang ký
  | 'completed'       // Hoàn tất
  | 'rejected'        // Từ chối
  | 'expired'         // Hết hạn
  | 'cancelled';      // Hủy bỏ

export type WorkflowType = 'sequential' | 'parallel';

export interface DocumentSettings {
  allow_download: boolean;
  allow_print: boolean;
  watermark_text?: string;
  notify_on_sign: boolean;
  notify_on_complete: boolean;
  reminder_days: number;
  signing_date?: string | null;
  signature_positions: SignaturePosition[];
}

// ============================================
// Signature
// ============================================

export type SignatureType = 'draw' | 'image' | 'digital' | 'text';

export interface SignaturePosition {
  id: string;
  page: number; // 1-indexed
  x: number; // percentage 0-100
  y: number; // percentage 0-100
  width: number; // percentage
  height: number; // percentage
  signer_id: string; // DocumentSigner id
  required: boolean;
  label?: string; // e.g., "Ký tên", "Ngày", "Chức danh"
}

export interface DocumentSigner {
  id: string;
  document_id: string;
  user_id?: string; // null if external signer
  email: string;
  full_name: string;
  role: SignerRole;
  /** 0-based position in a sequential workflow (DB: order_index) */
  order_index: number;
  /** @deprecated use order_index */
  order?: number;
  status: SignerStatus;
  signature_type?: SignatureType;
  signature_data?: SignatureData;
  signed_at?: string;
  ip_address?: string;
  user_agent?: string;
  decline_reason?: string;
  invited_at: string;
  reminded_at?: string;
  completed_at?: string;
}

export type SignerRole = 'signer' | 'approver' | 'cc' | 'witness';

export type SignerStatus =
  | 'pending'       // Chờ mời/ky
  | 'invited'       // Đã gửi lời mời
  | 'viewed'        // Đã xem tài liệu
  | 'signed'        // Đã ký
  | 'declined'      // Từ chối ký
  | 'expired';      // Hết hạn ký

export interface SignatureData {
  // For draw signature
  strokes?: StrokeData[];

  // Rasterised PNG (data URL) used when stamping into the PDF
  dataUrl?: string;

  // For image signature
  image_r2_key?: string;
  image_width?: number;
  image_height?: number;

  // For digital signature (PKI)
  certificate_pem?: string;
  certificate_issuer?: string;
  certificate_serial?: string;
  certificate_valid_from?: string;
  certificate_valid_to?: string;
  signature_value?: string; // Base64 encoded CMS/PKCS#7
  signed_hash?: string; // Hash of document at signing time

  // For text signature (typed name)
  text?: string;
  font_family?: string;
  font_size?: number;
  color?: string;

  // Common metadata
  signed_at?: string;
  signer_ip?: string;
  signer_user_agent?: string;
  geolocation?: GeoLocation;
}

export interface StrokeData {
  points: PointData[];
  color: string;
  width: number;
}

export interface PointData {
  x: number;
  y: number;
  pressure?: number;
  timestamp: number;
}

export interface GeoLocation {
  country?: string;
  region?: string;
  city?: string;
  latitude?: number;
  longitude?: number;
}

// ============================================
// Audit Log
// ============================================

export interface AuditLog {
  id: string;
  document_id: string;
  user_id?: string;
  action: AuditAction;
  details: Record<string, unknown>;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
}

export type AuditAction =
  | 'document_created'
  | 'document_updated'
  | 'document_deleted'
  | 'document_downloaded'
  | 'document_viewed'
  | 'signer_invited'
  | 'signer_reminded'
  | 'signer_viewed'
  | 'signer_signed'
  | 'signer_declined'
  | 'signer_expired'
  | 'signature_added'
  | 'signature_verified'
  | 'workflow_completed'
  | 'workflow_cancelled'
  | 'settings_changed';

// ============================================
// Notification
// ============================================

export interface Notification {
  id: string;
  user_id: string;
  type: NotificationType;
  title: string;
  message: string;
  reference_id?: string; // document_id, etc.
  reference_type?: 'document' | 'signer' | 'organization';
  read_at?: string;
  created_at: string;
}

export type NotificationType =
  | 'document_assigned'
  | 'document_signed'
  | 'document_completed'
  | 'document_declined'
  | 'document_expired'
  | 'reminder_to_sign'
  | 'invitation_accepted'
  | 'mention';

// ============================================
// API Request/Response
// ============================================

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: ApiError;
  meta?: ResponseMeta;
}

export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

export interface ResponseMeta {
  page?: number;
  limit?: number;
  total?: number;
  total_pages?: number;
}

export interface PaginatedResponse<T> extends ApiResponse<T[]> {
  meta: ResponseMeta;
}

// ============================================
// File Upload
// ============================================

export interface PresignedUploadUrl {
  upload_url: string;
  r2_key: string;
  expires_at: string;
  method: 'PUT' | 'POST';
  headers: Record<string, string>;
}

export interface FileUploadResult {
  r2_key: string;
  file_name: string;
  file_size: number;
  file_hash: string;
  content_type: string;
}

// ============================================
// Webhook
// ============================================

export interface WebhookEvent {
  id: string;
  organization_id: string;
  event: WebhookEventType;
  payload: Record<string, unknown>;
  created_at: string;
  delivered_at?: string;
  attempts: number;
  last_error?: string;
}

export type WebhookEventType =
  | 'document.created'
  | 'document.updated'
  | 'document.completed'
  | 'document.declined'
  | 'document.expired'
  | 'signer.invited'
  | 'signer.signed'
  | 'signer.declined';

// ============================================
// Search & Filter
// ============================================

export interface DocumentFilter {
  organization_id?: string;
  status?: DocumentStatus[];
  type?: DocumentType[];
  created_by?: string;
  signer_email?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
  sort_by?: 'created_at' | 'updated_at' | 'title' | 'status';
  sort_order?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}